"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { CheckCircle2, Edit3 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const formSchema = z.object({
  description: z.string().max(500, "Description must be 500 characters or less").optional(),
  type: z.enum(["ONE_TIME", "UNLIMITED"]),
});

export type FormValues = z.infer<typeof formSchema>;

interface LinkInviteTabProps {
  onSubmit: (data: FormValues) => void;
  submitting: boolean;
}

export function LinkInviteTab({ onSubmit, submitting }: LinkInviteTabProps) {
  const { t } = useTranslation(["workspaces", "common"]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: "",
      type: "ONE_TIME",
    },
  });

  const handleSubmit = (data: FormValues) => {
    onSubmit(data);
  };

  return (
    <Card className="flex h-full flex-col border-none bg-transparent shadow-none">
      <CardHeader className="flex-shrink-0 px-0 pt-0 pb-4">
        <CardTitle className="text-xl font-bold">{t("workspaces:createWorkspace")}</CardTitle>
        <CardDescription className="text-sm">
          {t("workspaces:createWorkspaceDescription")}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 px-0">
        <Form {...form}>
          <motion.form
            className="flex h-full flex-col space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {/* Invitation Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2 text-base">
                    <CheckCircle2 className="h-5 w-5" />
                    {t("workspaces:invitationType.label")}
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="h-12 text-base">
                        <SelectValue placeholder={t("workspaces:invitationType.label")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ONE_TIME">
                        {t("workspaces:invitationType.one_time")}
                      </SelectItem>
                      <SelectItem value="UNLIMITED">
                        {t("workspaces:invitationType.unlimited")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Message Field */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="flex items-center gap-2 text-base">
                    <Edit3 className="h-5 w-5" />
                    {t("workspaces:message")}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-20 resize-none text-base"
                      maxLength={500}
                      placeholder={t("workspaces:workspaceDescriptionPlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="flex justify-end">
                    <span
                      className={`font-mono ${field.value && field.value.length > 400 ? "text-amber-500" : ""}`}
                    >
                      {field?.value?.length ?? 0}/500
                    </span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              className="h-10 w-full text-base"
              disabled={submitting}
              size="lg"
              type="submit"
              variant="foreground"
            >
              {submitting ? t("common:loading") : t("workspaces:createInvitation")}
            </Button>
          </motion.form>
        </Form>
      </CardContent>
    </Card>
  );
}
